import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Sidebar } from "primereact/sidebar";

import { FiPlus, FiCheckCircle, FiLoader, FiAlertTriangle } from "react-icons/fi";
import { MdOutlinePushPin, MdOutlineInfo } from "react-icons/md";

import PinnedCard from "./PinnedCard";
import AddPinnedListings from "./AddPinnedListings";
import {
  usePinnedItems,
  useReorderPinnedItems,
  useRemovePinnedItem,
} from "../../hooks/pinned";
import { useDragReorder } from "../../hooks/useDragReorder";
import { useSignedUrlMap } from "../../hooks/useSignedUrlMap";

const SECTIONS = [
  {
    key: "venues",
    label: "Venues",
    // Venues and restaurants share one sequence because the public listing
    // merges both collections before ordering by position.
    blurb: "Venues and restaurants, in one shared order",
  },
  {
    key: "hotels",
    label: "Hotels",
    blurb: "Hotel listings, ordered ahead of the rest",
  },
];

const SaveStatus = ({ state }) => {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
        <FiLoader className="animate-spin" /> Saving order...
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
        <FiCheckCircle /> Order saved
      </span>
    );
  }
  return null;
};

const EmptyState = ({ label, canManage, onAdd }) => (
  <div className="bg-white rounded-xl border border-dashed border-gray-200 py-16 px-6 text-center">
    <span className="mx-auto w-14 h-14 rounded-full bg-primary8 text-primary flex items-center justify-center">
      <MdOutlinePushPin className="text-2xl" />
    </span>
    <h3 className="mt-4 text-base font-semibold text-gray-800">
      No pinned {label.toLowerCase()} yet
    </h3>
    <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
      Pinned listings are shown ahead of the regular results. Pin a few, then drag the
      cards to decide the order they appear in.
    </p>
    {canManage && (
      <button
        type="button"
        onClick={onAdd}
        className="mt-5 inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:brightness-95"
      >
        <FiPlus /> Pin {label.toLowerCase()}
      </button>
    )}
  </div>
);

const ConfirmUnpinDialog = ({ item, isPending, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
          <FiAlertTriangle className="text-lg" />
        </span>
        <h2 className="text-lg font-semibold text-gray-800">Unpin listing</h2>
      </div>

      <p className="text-sm text-gray-600 mt-3">
        <span className="font-semibold">{item.name || "This listing"}</span> will drop back
        into the regular results, and the cards after it move up one position.
      </p>
      <p className="text-xs text-gray-400 mt-3">
        The listing itself is not changed and can be pinned again at any time.
      </p>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isPending}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {isPending ? "Unpinning..." : "Unpin"}
        </button>
      </div>
    </div>
  </div>
);

const PinnedListings = () => {
  const [sectionKey, setSectionKey] = useState(SECTIONS[0].key);
  const [items, setItems] = useState([]);
  // The rail reference the local cards were last seeded from, so the render-time
  // sync below fires once per server change instead of on every render.
  const [syncedRail, setSyncedRail] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [isAdding, setIsAdding] = useState(false);
  const [unpinTarget, setUnpinTarget] = useState(null);

  const section = SECTIONS.find((entry) => entry.key === sectionKey);
  const savedNoticeTimer = useRef(null);

  useEffect(() => () => clearTimeout(savedNoticeTimer.current), []);

  // USER-role admins get the read-only view, matching the other admin screens.
  const canManage = localStorage.getItem("role") !== "USER";

  const { data: railItems, isLoading, error } = usePinnedItems(sectionKey);
  const { mutate: reorderPinnedItems } = useReorderPinnedItems(sectionKey);
  const { mutate: removePinnedItem, isPending: isUnpinning } =
    useRemovePinnedItem(sectionKey);

  const persistOrder = (order) => {
    setSaveState("saving");
    reorderPinnedItems(
      order.map((item) => ({ id: item._id, category: item.category })),
      {
        onSuccess: () => {
          setSaveState("saved");
          // The confirmation has served its purpose after a moment; leaving it
          // up would make the next drag look like it had already saved.
          clearTimeout(savedNoticeTimer.current);
          savedNoticeTimer.current = setTimeout(() => setSaveState("idle"), 2500);
        },
        onError: (orderError) => {
          setSaveState("idle");
          // A conflict already replaced the cached rail, which flows back into
          // `items` below, so the screen self-corrects to the server's order.
          toast.error(orderError.message);
        },
      }
    );
  };

  const { dragIndex, isDragging, getCardProps, moveBy } = useDragReorder({
    items,
    setItems,
    onCommit: persistOrder,
    disabled: !canManage,
  });

  // The server's rail is the source of truth, but it must not overwrite the
  // cards mid-drag or while a save is in flight — both are moments when the
  // screen is deliberately ahead of the server.
  const isBusy = isDragging || saveState === "saving";

  // Adopted during render rather than in an effect: an effect runs after paint,
  // which is one frame of the wrong cards every time the tab changes.
  if (!isBusy && railItems && railItems !== syncedRail) {
    setSyncedRail(railItems);
    setItems(railItems);
  }

  const images = useSignedUrlMap(items.map((item) => item.image));

  const changeSection = (key) => {
    if (key === sectionKey) return;
    setSectionKey(key);
    // Drop the cards and the sync marker so the other section's rail seeds
    // cleanly, rather than the previous one lingering under the new tab.
    setItems([]);
    setSyncedRail(null);
    setSaveState("idle");
  };

  const confirmUnpin = () => {
    removePinnedItem(
      { category: unpinTarget.category, id: unpinTarget._id },
      {
        onSuccess: (data) => {
          toast.success(data?.message || "Listing unpinned");
          setUnpinTarget(null);
        },
        onError: (unpinError) => {
          toast.error(unpinError.message);
          setUnpinTarget(null);
        },
      }
    );
  };

  return (
    <div className="px-5 pb-16 pt-4 flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pinned Listings</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pinned listings lead the public search results. Drag a card to change where it
            sits in the order.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:brightness-95"
          >
            <FiPlus /> Pin {section.label}
          </button>
        )}
      </div>

      {/* Section tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="inline-flex bg-gray-100 rounded-xl p-1 self-start">
          {SECTIONS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => changeSection(entry.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                entry.key === sectionKey
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {entry.label}
              {entry.key === sectionKey && items.length > 0 && (
                <span className="ml-2 text-xs font-semibold text-gray-400">
                  {items.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <SaveStatus state={saveState} />
          <p className="text-xs text-gray-400">{section.blurb}</p>
        </div>
      </div>

      {/* How the order is used, so a drag that changes nothing public is not a
          mystery. The venues listing reshuffles its pinned rail daily. */}
      {sectionKey === "venues" && (
        <div className="flex gap-2.5 bg-sky-50 border border-sky-200 rounded-lg px-4 py-3">
          <MdOutlineInfo className="text-sky-600 mt-0.5 shrink-0" />
          <p className="text-xs text-sky-900">
            This order is what the venue search reads from the database, but that endpoint
            shuffles its pinned results once a day before showing them, so the public order
            will not match these cards exactly. The hotel listings do follow this order
            directly.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error.message}
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse"
            >
              <div className="h-36 bg-gray-100" />
              <div className="p-3 space-y-2">
                <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="h-8 bg-gray-50 rounded mt-3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <EmptyState
          label={section.label}
          canManage={canManage}
          onAdd={() => setIsAdding(true)}
        />
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item, index) => (
            <PinnedCard
              key={item._id}
              item={item}
              index={index}
              total={items.length}
              image={images.get(item.image)}
              isDragging={dragIndex === index}
              canManage={canManage}
              dragProps={getCardProps(index)}
              onMoveUp={() => moveBy(index, -1)}
              onMoveDown={() => moveBy(index, 1)}
              onUnpin={() => setUnpinTarget(item)}
            />
          ))}
        </div>
      )}

      {items.length > 1 && canManage && (
        <p className="text-xs text-gray-400 text-center">
          Drag a card to where it should sit, or use the arrows to nudge it one step.
          Changes save on their own.
        </p>
      )}

      {unpinTarget && (
        <ConfirmUnpinDialog
          item={unpinTarget}
          isPending={isUnpinning}
          onCancel={() => setUnpinTarget(null)}
          onConfirm={confirmUnpin}
        />
      )}

      <Sidebar
        visible={isAdding}
        position="right"
        onHide={() => setIsAdding(false)}
        className="w-full sm:w-[520px] md:w-[560px]"
        dismissable
        modal
      >
        <AddPinnedListings
          section={sectionKey}
          sectionLabel={section.label}
          onPinned={() => setIsAdding(false)}
          onCancel={() => setIsAdding(false)}
        />
      </Sidebar>
    </div>
  );
};

export default PinnedListings;
